import type Lenis from "lenis";
import type { ComponentRef } from "react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { ReactLenis } from "lenis/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLocation } from "react-router-dom";
import { paths } from "../../../routes/paths";
import Footer from "../footer/footer";
import Navbar from "../navbar/navbar";
import {
  ScrollContextProvider,
  flushScrollFrame,
} from "../../../lib/scroll";
import { LAYOUT_DESKTOP_MIN_WIDTH_PX } from "../../../const/const";

gsap.registerPlugin(ScrollTrigger);
gsap.ticker.lagSmoothing(0);
ScrollTrigger.config({ ignoreMobileResize: true });

const DEFAULT_SEO_TITLE = "CREON AGENCY";
const DEFAULT_SEO_DESCRIPTION =
  "ОТ СТРАТЕГИИ И КРЕАТИВНОЙ КОНЦЕПЦИИ ДО РЕАЛИЗАЦИИ. КОМАНДА ПРОФЕССИОНАЛОВ В КРЕАТИВЕ, ДИЗАЙНЕ И МАРКЕТИНГЕ, ОБЪЕДИНЁННЫХ ОБЩИМИ ЦЕННОСТЯМИ.";

const getLenisOptionsForWidth = (width: number) => {
  let lerp = 0.04;
  let wheelMultiplier = 0.7;
  let touchMultiplier = 0.37375;
  let syncTouchLerp = 0.082;

  if (width < LAYOUT_DESKTOP_MIN_WIDTH_PX) {
    lerp = 0.12;
    wheelMultiplier = 0.85;
    touchMultiplier = 0.768;
    syncTouchLerp = 0.075;
  } else if (width > 1920) {
    lerp = 0.04;
    wheelMultiplier = 0.7;
    touchMultiplier = 0.48;
  }

  const syncTouch = width < LAYOUT_DESKTOP_MIN_WIDTH_PX;

  return {
    lerp,
    wheelMultiplier,
    touchMultiplier,
    smoothWheel: true,
    autoRaf: false,
    syncTouch,
    syncTouchLerp,
  };
};

const getLenisOptions = () => getLenisOptionsForWidth(window.innerWidth);

function applyLenisOptionsFromWidth(
  lenis: Lenis,
  width: number,
) {
  const o = getLenisOptionsForWidth(width);
  Object.assign(lenis.options, {
    lerp: o.lerp,
    duration: undefined,
    easing: undefined,
    wheelMultiplier: o.wheelMultiplier,
    touchMultiplier: o.touchMultiplier,
    smoothWheel: o.smoothWheel,
    autoRaf: o.autoRaf,
    syncTouch: o.syncTouch,
    syncTouchLerp: o.syncTouchLerp,
  });
}

type Props = {
  children: ReactNode;
};

const Layout = ({ children }: Props) => {
  const location = useLocation();
  const [navbarColor, setNavbarColor] = useState<"white" | "blue">("white");
  const [isFooterFormVisible, setFooterFormVisible] = useState(true);
  const [isFooterVisible, setFooterVisible] = useState(true);
  const [lenisOptions] = useState(getLenisOptions);
  const lenisRef = useRef<ComponentRef<typeof ReactLenis>>(null);
  const resizeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedInnerRef = useRef({ w: 0, h: 0 });
  const activeTouchCountRef = useRef(0);
  const touchQuietUntilRef = useRef(0);
  const touchInertiaQuietMs = 520;
  const mobileChromeUiHeightDeltaMaxPx = 140;

  useEffect(() => {
    const runResizePreserveScroll = () => {
      const lenis = lenisRef.current?.lenis;
      const y = lenis?.scroll ?? window.scrollY;
      if (lenis) {
        applyLenisOptionsFromWidth(lenis, window.innerWidth);
        lenis.resize();
      }
      ScrollTrigger.refresh();
      const applyY = () => {
        const l = lenisRef.current?.lenis;
        if (!l) {
          window.scrollTo(0, y);
          return;
        }
        const maxY = ScrollTrigger.maxScroll(document.documentElement);
        const clamped =
          Number.isFinite(maxY) && maxY > 0
            ? Math.max(0, Math.min(y, maxY))
            : y;
        l.scrollTo(clamped, { immediate: true });
      };
      applyY();
      requestAnimationFrame(() => {
        requestAnimationFrame(applyY);
      });
      lastCommittedInnerRef.current = {
        w: window.innerWidth,
        h: window.innerHeight,
      };
    };

    const shouldSkipForTouch = () => {
      const w = window.innerWidth;
      const now = Date.now();
      return (
        w < LAYOUT_DESKTOP_MIN_WIDTH_PX &&
        (activeTouchCountRef.current > 0 || now < touchQuietUntilRef.current)
      );
    };

    const isMobileChromeUiHeightOnlySkip = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const last = lastCommittedInnerRef.current;
      if (w >= LAYOUT_DESKTOP_MIN_WIDTH_PX) {
        return false;
      }
      if (last.w === 0) {
        return false;
      }
      if (w !== last.w) {
        return false;
      }
      if (h === last.h) {
        return false;
      }
      return Math.abs(h - last.h) <= mobileChromeUiHeightDeltaMaxPx;
    };

    const bumpTouchQuiet = () => {
      touchQuietUntilRef.current = Date.now() + touchInertiaQuietMs;
    };

    const onTouchStart = () => {
      activeTouchCountRef.current += 1;
    };

    const onTouchEnd = () => {
      activeTouchCountRef.current = Math.max(
        0,
        activeTouchCountRef.current - 1,
      );
      bumpTouchQuiet();
    };

    const handleResize = () => {
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current);
      }
      resizeDebounceRef.current = setTimeout(() => {
        resizeDebounceRef.current = null;
        if (shouldSkipForTouch()) {
          return;
        }
        const w = window.innerWidth;
        const h = window.innerHeight;
        const last = lastCommittedInnerRef.current;
        if (last.w !== 0 && w === last.w && h === last.h) {
          return;
        }
        if (isMobileChromeUiHeightOnlySkip()) {
          lastCommittedInnerRef.current = { w, h };
          return;
        }
        runResizePreserveScroll();
      }, 165);
    };

    window.addEventListener("resize", handleResize, { passive: true });
    window.visualViewport?.addEventListener("resize", handleResize);
    window.addEventListener("touchstart", onTouchStart, {
      capture: true,
      passive: true,
    });
    window.addEventListener("touchend", onTouchEnd, {
      capture: true,
      passive: true,
    });
    window.addEventListener("touchcancel", onTouchEnd, {
      capture: true,
      passive: true,
    });
    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.removeEventListener("touchstart", onTouchStart, true);
      window.removeEventListener("touchend", onTouchEnd, true);
      window.removeEventListener("touchcancel", onTouchEnd, true);
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current);
        resizeDebounceRef.current = null;
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    const lenis = lenisRef.current?.lenis;
    if (lenis) {
      applyLenisOptionsFromWidth(lenis, window.innerWidth);
      lenis.resize();
    }
    ScrollTrigger.refresh();
  }, [location.pathname]);

  useEffect(() => {
    const scrollTop = () => {
      const isMainMobile =
        location.pathname === paths.MainPage &&
        window.innerWidth < LAYOUT_DESKTOP_MIN_WIDTH_PX;
      const targetY = isMainMobile ? 1 : 0;
      window.scrollTo(0, targetY);
      lenisRef.current?.lenis?.scrollTo(targetY, { immediate: true });
    };
    if (location.pathname === paths.MainPage) {
      requestAnimationFrame(scrollTop);
      return;
    }
    scrollTop();
  }, [location.pathname]);

  useEffect(() => {
    switch (location.pathname) {
      case "/":
        setNavbarColor("blue");
        setFooterFormVisible(true);
        setFooterVisible(false);
        break;

      case `/${paths.SelfEmployedFormPage}`:
        setNavbarColor("white");
        setFooterFormVisible(false);
        break;

      case `/${paths.ServicesPage}`:
      case `/${paths.ServicesEventMarketingPage}`:
      case `/${paths.ServicesSportMarketingPage}`:
      case `/${paths.ServicesConsumerPromoPage}`:
      case `/${paths.AboutUsPage}`:
      case `/${paths.TenderhubPage}`:
        setNavbarColor("white");
        setFooterFormVisible(true);
        setFooterVisible(true);
        break;

      case `/${paths.ContactsPage}`:
        setNavbarColor("white");
        setFooterFormVisible(false);
        setFooterVisible(true);
        break;

      default:
        setNavbarColor("white");
        setFooterVisible(false);
    }

    if (location.pathname.includes(paths.CasesPage)) {
      setFooterFormVisible(true);
      setFooterVisible(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const isCaseDetailsPage =
      location.pathname.startsWith(`/${paths.CasesPage}/`) &&
      location.pathname !== `/${paths.CasesPage}`;
    if (isCaseDetailsPage) {
      return;
    }
    document.title = DEFAULT_SEO_TITLE;
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    if (description) {
      description.setAttribute("content", DEFAULT_SEO_DESCRIPTION);
    }
  }, [location.pathname]);

  useEffect(() => {
    let rafId = 0;
    const tick = (timeMs: number) => {
      const lenis = lenisRef.current?.lenis;
      lenis?.raf(timeMs);
      ScrollTrigger.update();
      flushScrollFrame(lenis?.scroll ?? window.scrollY);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let rafId = 0;
    const scroller = document.documentElement;

    const bindLenisScrollTrigger = () => {
      if (cancelled) {
        return;
      }
      const lenis = lenisRef.current?.lenis;
      if (!lenis) {
        rafId = requestAnimationFrame(bindLenisScrollTrigger);
        return;
      }

      ScrollTrigger.scrollerProxy(scroller, {
        scrollTop() {
          const v = arguments[0];
          if (arguments.length > 0 && typeof v === "number") {
            lenis.scrollTo(v, { immediate: true });
          }
          return lenis.scroll;
        },
        scrollLeft() {
          return 0;
        },
        getBoundingClientRect() {
          const vv = window.visualViewport;
          if (vv && window.innerWidth < LAYOUT_DESKTOP_MIN_WIDTH_PX) {
            return {
              top: 0,
              left: 0,
              width: vv.width,
              height: vv.height,
            };
          }
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          };
        },
      });

      ScrollTrigger.defaults({ scroller });
      ScrollTrigger.refresh();
    };

    bindLenisScrollTrigger();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      ScrollTrigger.defaults({ scroller: window });
      ScrollTrigger.scrollerProxy(scroller);
      ScrollTrigger.refresh();
    };
  }, []);

  return (
    <>
      <ReactLenis root options={lenisOptions} ref={lenisRef} />
      <ScrollContextProvider enableNativeScrollListener>
        <div className="layout">
          <Navbar theme={navbarColor} />

          {children}

          <Footer
            isFooterVisible={isFooterVisible}
            isFooterFormVisible={isFooterFormVisible}
          />
        </div>
      </ScrollContextProvider>
    </>
  );
};

export default Layout;
